import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  IconButton,
  TableSortLabel,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as CsvIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { exportTardiesToCSV, exportTardiesToExcel, exportTardiesToPDF } from '../utils/exportUtils';
import { TimezoneService } from '../services/timezoneService';
import moment from 'moment-timezone';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';

interface TardiesReportFilters {
  startDate: string;
  endDate: string;
  userId: string;
  departmentId: string;
  campaignId: string;
  status: string;
  page: number;
  limit: number;
}

interface TardyRecord {
  id: string;
  userId: string;
  employeeId: string;
  employeeName: string;
  email: string;
  role: string;
  department: string;
  departmentId: string;
  date: string;
  scheduledStartTime: string;
  actualClockInTime: string;
  minutesLate: number;
  status: 'on-time' | 'late';
  campaignId: string;
  campaignName: string;
  timestamp: string;
  scheduledStartTimestamp?: string;
  actualClockInTimestamp?: string;
  userTimezone: string;
}

const TardiesReportPage: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canUpdateTimeEntries = permissions.includes('time_entries.update');
  const navigate = useNavigate();
  const [filters, setFilters] = useState<TardiesReportFilters>({
    startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    userId: '',
    departmentId: '',
    campaignId: '',
    status: '',
    page: 1,
    limit: 50,
  });
  const [showFilters, setShowFilters] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'employee' | 'date' | 'minutesLate' | 'status' | 'department' | 'campaign'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc'); // newest first by default

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Fetch tardies report data
  const { data: tardiesData, isLoading, error, refetch } = useQuery({
    queryKey: ['tardiesReport', filters],
    queryFn: async () => {
      try {
        const params: any = {
          startDate: filters.startDate,
          endDate: filters.endDate,
          page: filters.page,
          limit: filters.limit,
        };
        
        if (filters.userId) params.userId = filters.userId;
        if (filters.departmentId) params.departmentId = filters.departmentId;
        if (filters.campaignId) params.campaignId = filters.campaignId;
        if (filters.status) params.status = filters.status;
        
        const response = await apiClient.timeEntries.getTardiesReport(params);
        return response.data;
      } catch (err: any) {
        console.error('Error fetching tardies report:', err);
        console.error('Error details:', {
          message: err?.message,
          response: err?.response?.data,
          status: err?.response?.status
        });
        throw err;
      }
    },
    enabled: ['manager', 'administrator', 'super_user'].includes(user?.role || ''),
    retry: 1,
  });

  // Fetch users for filter dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.users.getAll(),
    enabled: ['manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch departments for filter dropdown
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => apiClient.departments.getAll(),
    enabled: ['manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch campaigns for filter dropdown
  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns', filters.startDate, filters.endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: filters.startDate,
      includeWithActivityTo: filters.endDate,
    }),
    enabled: ['manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  const handleFilterChange = (field: keyof TardiesReportFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      userId: '',
      departmentId: '',
      campaignId: '',
      status: '',
      page: 1,
      limit: 50,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilters(prev => ({ ...prev, page: value }));
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const fetchAllTardiesForExport = async () => {
    const baseParams: any = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      page: 1,
      limit: 1000,
    };

    if (filters.userId) baseParams.userId = filters.userId;
    if (filters.departmentId) baseParams.departmentId = filters.departmentId;
    if (filters.campaignId) baseParams.campaignId = filters.campaignId;
    if (filters.status && filters.status !== 'all') baseParams.status = filters.status;

    let allRecords: any[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      const response = await apiClient.timeEntries.getTardiesReport({
        ...baseParams,
        page
      });
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allRecords = allRecords.concat(pageData);
      total = pagination?.total ?? allRecords.length;

      if (allRecords.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    return allRecords;
  };

  const handleExportCSV = async () => {
    try {
      const data = await fetchAllTardiesForExport();
      exportTardiesToCSV(data, filters);
    } catch (error) {
      console.error('Error exporting tardies CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportExcel = async () => {
    try {
      const data = await fetchAllTardiesForExport();
      exportTardiesToExcel(data, filters);
    } catch (error) {
      console.error('Error exporting tardies Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportPDF = async () => {
    try {
      const data = await fetchAllTardiesForExport();
      exportTardiesToPDF(data, filters);
    } catch (error) {
      console.error('Error exporting tardies PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'late': return 'error';
      case 'on-time': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'late': return 'Late';
      case 'on-time': return 'On-Time';
      default: return status;
    }
  };

  // Format times using employee's timezone
  const formatTime = (tardy: TardyRecord, field: 'scheduledStartTime' | 'actualClockInTime') => {
    // Use raw timestamp if available, otherwise use formatted string from backend
    const rawTimestamp = field === 'scheduledStartTime' 
      ? tardy.scheduledStartTimestamp 
      : tardy.actualClockInTimestamp;
    
    if (rawTimestamp) {
      // Format using employee's timezone
      return TimezoneService.formatDateTimeConsistent(rawTimestamp, tardy.userTimezone);
    }
    
    // Fallback to backend-formatted time
    return tardy[field];
  };

  const toggleSort = (col: 'employee' | 'date' | 'minutesLate' | 'status' | 'department' | 'campaign') => {
    if (sortBy === col) setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir(col === 'date' ? 'desc' : 'asc');
    }
  };

  const handleEditClick = (tardy: TardyRecord) => {
    // Navigate to time records management page with edit entry parameter
    // The entry ID is the clock-in entry ID
    navigate(`/management/time-records?editEntry=${tardy.id}&date=${tardy.date}`);
  };

  const sortedTardies = React.useMemo(() => {
    const list = tardiesData?.data ? [...tardiesData.data] : [];
    list.sort((a: TardyRecord, b: TardyRecord) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'employee':
          av = a.employeeName.toLowerCase();
          bv = b.employeeName.toLowerCase();
          break;
        case 'date':
          av = new Date(a.date).getTime();
          bv = new Date(b.date).getTime();
          break;
        case 'minutesLate':
          av = a.minutesLate || 0;
          bv = b.minutesLate || 0;
          break;
        case 'status':
          av = a.status;
          bv = b.status;
          break;
        case 'department':
          av = (a.department || '').toLowerCase();
          bv = (b.department || '').toLowerCase();
          break;
        case 'campaign':
          av = (a.campaignName || '').toLowerCase();
          bv = (b.campaignName || '').toLowerCase();
          break;
        default:
          av = new Date(a.timestamp).getTime();
          bv = new Date(b.timestamp).getTime();
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [tardiesData?.data, sortBy, sortDir]);

  const activeFilters = Object.entries(filters).filter(([key, value]) => 
    value !== '' && value !== 0 && value !== 'all' && key !== 'page' && key !== 'limit'
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Tardies Report
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
            </IconButton>
          </Box>

          {showFilters && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={usersData?.data?.data || []}
                  value={usersData?.data?.data?.find((u: any) => u.id === filters.userId) || null}
                  onChange={(event, newValue) => {
                    handleFilterChange('userId', newValue?.id || '');
                  }}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.employeeId})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee"
                      placeholder="Search employee..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.firstName} {option.lastName} ({option.employeeId})
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No employees found"
                  clearOnEscape
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Department"
                  value={filters.departmentId}
                  onChange={(value) => handleFilterChange('departmentId', String(value))}
                  options={(departmentsData?.data?.data || []).map((dept: any) => ({
                    value: dept.id,
                    label: dept.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Campaign"
                  value={filters.campaignId}
                  onChange={(value) => handleFilterChange('campaignId', String(value))}
                  options={(campaignsData?.data?.data || []).map((campaign: any) => ({
                    value: campaign.id,
                    label: campaign.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Status"
                  value={filters.status}
                  onChange={(value) => handleFilterChange('status', String(value))}
                  options={[
                    { value: 'on-time', label: 'On-Time' },
                    { value: 'late', label: 'Late' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  onClick={handleResetFilters}
                  fullWidth
                >
                  Reset Filters
                </Button>
              </Grid>
            </Grid>
          )}

          {activeFilters.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {activeFilters.map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  onDelete={() => handleFilterChange(key as keyof TardiesReportFilters, key === 'status' ? 'all' : '')}
                  size="small"
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Tardies Records
              {tardiesData?.pagination && (
                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
                  ({tardiesData.pagination.total} total)
                </Typography>
              )}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<ArrowDropDownIcon />}
                onClick={handleExportMenuOpen}
                sx={{ mr: 1 }}
              >
                Export
              </Button>
              {canShareReports && (
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => setShareDialogOpen(true)}
                >
                  Share
                </Button>
              )}
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={handleExportCSV}>
                  <ListItemIcon>
                    <CsvIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Export as CSV</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleExportExcel}>
                  <ListItemIcon>
                    <ExcelIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Export as Excel</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleExportPDF}>
                  <ListItemIcon>
                    <PdfIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Export as PDF</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            >
              Failed to load tardies report. Please try again.
              {(process.env.NODE_ENV === 'development' || (window as any).__DEV__) && (
                <Typography variant="caption" display="block" sx={{ mt: 1, fontFamily: 'monospace' }}>
                  Error: {(error as any)?.message || (error as any)?.response?.data?.error || 'Unknown error'}
                </Typography>
              )}
            </Alert>
          )}

          {!isLoading && !error && sortedTardies.length === 0 && (
            <Alert severity="info">
              No tardies records found for the selected filters.
            </Alert>
          )}

          {!isLoading && !error && sortedTardies.length > 0 && (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'employee'}
                          direction={sortBy === 'employee' ? sortDir : 'asc'}
                          onClick={() => toggleSort('employee')}
                        >
                          Employee
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'date'}
                          direction={sortBy === 'date' ? sortDir : 'desc'}
                          onClick={() => toggleSort('date')}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'minutesLate'}
                          direction={sortBy === 'minutesLate' ? sortDir : 'asc'}
                          onClick={() => toggleSort('minutesLate')}
                        >
                          Minutes Late
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'status'}
                          direction={sortBy === 'status' ? sortDir : 'asc'}
                          onClick={() => toggleSort('status')}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'department'}
                          direction={sortBy === 'department' ? sortDir : 'asc'}
                          onClick={() => toggleSort('department')}
                        >
                          Department
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'campaign'}
                          direction={sortBy === 'campaign' ? sortDir : 'asc'}
                          onClick={() => toggleSort('campaign')}
                        >
                          Campaign
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Scheduled Start</TableCell>
                      <TableCell>Actual Clock-In</TableCell>
                      {canUpdateTimeEntries && <TableCell>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedTardies.map((tardy: TardyRecord) => (
                      <TableRow key={tardy.id}>
                        <TableCell>
                          {tardy.employeeName}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {tardy.employeeId}
                          </Typography>
                        </TableCell>
                        <TableCell>{tardy.date}</TableCell>
                        <TableCell>
                          {tardy.minutesLate > 0 ? (
                            <Typography color="error" fontWeight="bold">
                              {tardy.minutesLate} min
                            </Typography>
                          ) : (
                            <Typography color="success.main">0 min</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(tardy.status)}
                            color={getStatusColor(tardy.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{tardy.department}</TableCell>
                        <TableCell>{tardy.campaignName}</TableCell>
                        <TableCell>
                          {formatTime(tardy, 'scheduledStartTime')}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {tardy.userTimezone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatTime(tardy, 'actualClockInTime')}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {tardy.userTimezone}
                          </Typography>
                        </TableCell>
                        {canUpdateTimeEntries && (
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(tardy)}
                              color="primary"
                              title="Edit Time Entry"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {tardiesData?.pagination && tardiesData.pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={tardiesData.pagination.totalPages}
                    page={tardiesData.pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="tardies"
          reportFilters={filters}
          reportTitle="Tardies Report"
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default TardiesReportPage;

